import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import PropTypes from 'prop-types';
import {
  Formik,
  Form,
  useField,
  useFormikContext,
} from 'formik';
import * as Sentry from '@sentry/browser';
import format from 'string-template';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';

import GenericErrorMessage from '../../../../../components/GenericErrorMessage';
import useAuthentication from '../../../../../hooks/useAuthentication';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useNavigation from '../../../../../hooks/useNavigation';
import SubmitButton from '../SubmitButton';
import FormikLabeledInput from '../Input';
import FormikPhoneInput from '../../../../../components/FormikInput/FormikPhoneInput';
import TermsAgreement from '../TermsAgreement';

import {
  Container,
  SignUpContent,
  ErrorContainer,
  StyledLinkButton,
  NameContainer,
  StyledTitle,
  HelperTextContainer,
  StyledLockIcon,
  StyledIconButton,
  PersonalDataInputContainer,
  SignInActionsContainer,
  ResetPasswordMessage,
} from './styles';

import { getInitialValues, getValidationSchema } from './validation';
import texts from './texts.json';

/** The terms tick box as a field of the account form. */
const FormikTermsAgreement = ({
  contractTerms,
  termsKey,
  coachConditions,
  color,
  isDarkTheme,
}) => {
  const [field, meta, helpers] = useField('hasAgreedToTerms');
  /*
    The contract's terms can arrive after the form first renders, so the field may be missing from the values Formik
    marks touched on submit: a submit attempt is what says the client has been told.
  */
  const { submitCount } = useFormikContext();
  // A tick given to other terms (a lead switching contract options) is not agreement to these.
  const tickedTermsKeyRef = useRef(termsKey);
  useEffect(() => {
    if (tickedTermsKeyRef.current !== termsKey) {
      tickedTermsKeyRef.current = termsKey;
      helpers.setValue(false);
    }
  }, [termsKey, helpers]);
  return (
    <TermsAgreement
      isChecked={!!field.value}
      onChange={(isChecked) => helpers.setValue(isChecked)}
      contractTerms={contractTerms}
      coachConditions={coachConditions}
      error={(meta.touched || submitCount > 0) && meta.error ? meta.error : ''}
      color={color}
      isDarkTheme={isDarkTheme}
    />
  );
};

FormikTermsAgreement.propTypes = {
  contractTerms: PropTypes.arrayOf(PropTypes.string).isRequired,
  termsKey: PropTypes.string.isRequired,
  coachConditions: PropTypes.string,
  color: PropTypes.string,
  isDarkTheme: PropTypes.bool.isRequired,
};

FormikTermsAgreement.defaultProps = {
  coachConditions: '',
  color: '',
};

const AccountCreation = ({
  email,
  firstName,
  lastName,
  phoneNumber,
  onSubmit,
  state,
  color,
  isDarkTheme,
  isSignInMode,
  onChangeEmail,
  termsAgreement,
}) => {
  const {
    isError,
    errorMsg,
    route,
    routeSearch,
    routeState,
  } = state;

  const [showPassword, setShowPassword] = useState(false);
  const [passwordResetStatus, setPasswordResetStatus] = useState(null);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const isComponentMountedRef = useComponentMounted();
  const { navigateTo } = useNavigation();
  const { sendPasswordResetEmail } = useAuthentication();

  const handleSubmit = useCallback(async (values, actions) => {
    await onSubmit(values);
    if (isComponentMountedRef.current) {
      actions.setSubmitting(false);
    }
  }, [
    onSubmit,
    isComponentMountedRef,
  ]);

  const handleNavigate = useCallback(() => {
    navigateTo(route, { search: routeSearch, state: routeState });
  }, [
    navigateTo,
    route,
    routeState,
    routeSearch,
  ]);

  const handleForgotPassword = useCallback(async (currentEmail) => {
    if (!currentEmail) {
      return;
    }
    try {
      await sendPasswordResetEmail(currentEmail);
      if (isComponentMountedRef.current) {
        setPasswordResetStatus({
          isError: false,
          text: format(texts.passwordResetSent, { email: currentEmail }),
        });
      }
    } catch (err) {
      Sentry.captureException(err);
      if (isComponentMountedRef.current) {
        setPasswordResetStatus({
          isError: true,
          text: texts.passwordResetError,
        });
      }
    }
  }, [sendPasswordResetEmail, isComponentMountedRef]);

  return (
    <Formik
      initialValues={{
        ...getInitialValues({
          email,
          firstName,
          lastName,
          phoneNumber,
        }),
        ...(!!termsAgreement && { hasAgreedToTerms: false }),
      }}
      validationSchema={getValidationSchema({ requiresTermsAgreement: !!termsAgreement })}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values }) => (
        <Form>
          <Container>
            <SignUpContent>
              { !isSignInMode && (
                <>
                  <StyledTitle $isDarkTheme={isDarkTheme}>
                    {texts.personalDetails}
                  </StyledTitle>
                  <PersonalDataInputContainer>
                    <NameContainer>
                      <FormikLabeledInput
                        name="firstName"
                        type="text"
                        autoComplete="first-name"
                        label={texts.label.firstName}
                        isHalfWidth
                        isDarkTheme={isDarkTheme}
                      />
                      <FormikLabeledInput
                        name="lastName"
                        type="text"
                        autoComplete="last-name"
                        label={texts.label.lastName}
                        isHalfWidth
                        isDarkTheme={isDarkTheme}
                      />
                    </NameContainer>
                    <FormikPhoneInput
                      name="phoneNumber"
                      label={texts.label.phoneNumber}
                      isDarkTheme={isDarkTheme}
                      autoComplete="tel"
                      className="onboarding-phone-input"
                    />
                  </PersonalDataInputContainer>
                  <StyledTitle $isDarkTheme={isDarkTheme}>
                    {texts.loginDetails}
                  </StyledTitle>
                </>
              )}
              <FormikLabeledInput
                name="email"
                autoComplete="email"
                label={texts.label.email}
                placeholder={texts.placeholder.email}
                isDarkTheme={isDarkTheme}
              />
              <FormikLabeledInput
                name="password"
                placeholder={texts.placeholder.password}
                label={texts.label.password}
                type={showPassword ? 'text' : 'password'}
                isDarkTheme={isDarkTheme}
                endAdornment={(
                  <InputAdornment position="end">
                    <StyledIconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      size="small"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </StyledIconButton>
                  </InputAdornment>
                )}
              />
              <HelperTextContainer>
                <StyledLockIcon />
                {texts.securePassword}
              </HelperTextContainer>
              {!!termsAgreement && (
                <FormikTermsAgreement
                  contractTerms={termsAgreement.contractTerms}
                  termsKey={termsAgreement.termsKey}
                  coachConditions={termsAgreement.coachConditions}
                  color={color}
                  isDarkTheme={isDarkTheme}
                />
              )}
              <SubmitButton
                isLoading={isSubmitting}
                type="submit"
                $background={color}
                $isDarkTheme={isDarkTheme}
              >
                {isSignInMode ? texts.signIn : texts.createAccount}
              </SubmitButton>
              { isError && (
                <ErrorContainer>
                  <GenericErrorMessage>{errorMsg}</GenericErrorMessage>
                  { route && (
                    <StyledLinkButton
                      onClick={handleNavigate}
                      $isDarkTheme={isDarkTheme}
                    >
                      {texts.loginWithEmail}
                    </StyledLinkButton>
                  )}
                </ErrorContainer>
              )}
              { isSignInMode && (
                <SignInActionsContainer>
                  <StyledLinkButton
                    type="button"
                    onClick={() => handleForgotPassword(values.email)}
                    $isDarkTheme={isDarkTheme}
                  >
                    {texts.forgotPassword}
                  </StyledLinkButton>
                  <StyledLinkButton
                    type="button"
                    onClick={onChangeEmail}
                    $isDarkTheme={isDarkTheme}
                  >
                    {texts.changeEmail}
                  </StyledLinkButton>
                  { passwordResetStatus && (
                    <ResetPasswordMessage $isError={passwordResetStatus.isError}>
                      {passwordResetStatus.text}
                    </ResetPasswordMessage>
                  )}
                </SignInActionsContainer>
              )}
            </SignUpContent>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

AccountCreation.propTypes = {
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  phoneNumber: PropTypes.string,
  onSubmit: PropTypes.func,
  state: PropTypes.object,
  color: PropTypes.string,
  isDarkTheme: PropTypes.bool.isRequired,
  isSignInMode: PropTypes.bool,
  onChangeEmail: PropTypes.func,
  /*
    The contract's terms when the client agrees to them on this form (the two-page checkout); without it the form
    has no tick box.
  */
  termsAgreement: PropTypes.shape({
    contractTerms: PropTypes.arrayOf(PropTypes.string).isRequired,
    coachConditions: PropTypes.string,
    // Which terms these are: when it changes, the tick is cleared.
    termsKey: PropTypes.string.isRequired,
  }),
};

AccountCreation.defaultProps = {
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  onSubmit: () => {},
  state: {},
  color: '',
  isSignInMode: false,
  onChangeEmail: () => {},
  termsAgreement: null,
};

export default AccountCreation;
